import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { EyeIcon, EyeOffIcon, LockIcon, Loader2Icon, ShieldCheckIcon } from 'lucide-react';
import { SITE_PASSWORD } from '../hooks/useSessionUnlock';
import { SalesSourceLogo } from './SalesSourceLogo';
import { ThemeToggle } from './ThemeToggle';
interface PasswordGateProps {
  onUnlock: () => void;
}
export function PasswordGate({
  onUnlock
}: PasswordGateProps) {
  const [value, setValue] = useState('');
  const [reveal, setReveal] = useState(false);
  const [status, setStatus] = useState<'idle' | 'checking' | 'error'>('idle');
  const inputRef = useRef<HTMLInputElement>(null);
  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (status === 'checking' || value.trim().length === 0) return;
    setStatus('checking');
    window.setTimeout(() => {
      if (value.trim() === SITE_PASSWORD) {
        onUnlock();
      } else {
        setStatus('error');
        setValue('');
        inputRef.current?.focus();
      }
    }, 520);
  }
  return <main className="grid-noise relative flex min-h-screen w-full items-center justify-center bg-shell-bg px-6 py-16" data-id="element-54" data-mp="2rhdi6dn1xli9">
      <div className="absolute right-6 top-6" data-id="element-55" data-mp="2wgqctkk5zwve">
        <ThemeToggle data-id="element-56" data-mp="31dqfrmlpwjia" />
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.45,
      ease: [0.22, 1, 0.36, 1]
    }} className="w-full max-w-md" data-id="element-57" data-mp="31cow4tqmyooy">
        <div className="mb-10 flex flex-col items-center text-center" data-id="element-58" data-mp="3acka16bksxso">
          <SalesSourceLogo size={72} withWordmark={false} data-id="element-59" data-mp="3fcgef6o6qr8r" />
          <span className="mt-7 inline-flex items-center gap-2 rounded-full border border-shell-border bg-shell-surface px-3 py-1 font-mono text-[10px] uppercase tracking-[0.22em] text-shell-dim" data-id="element-60" data-mp="3k4pyw76j6z3n">
            <LockIcon className="h-3 w-3 text-accent" aria-hidden="true" data-id="element-61" data-mp="3t5fv3bzperhw" />
            Private
          </span>
          <h1 className="mt-6 font-display text-5xl leading-none tracking-tight text-shell-text" data-id="element-62" data-mp="1ybm4bk00v6u">
            Sales Source
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-shell-dim" data-id="element-63" data-mp="1y2j8qbbgs02l">
            Internal access point for project tools, logins and shared documents.
            Enter the team password to continue.
          </p>
        </div>

        <motion.form onSubmit={handleSubmit} animate={status === 'error' ? {
        x: [0, -8, 7, -5, 0]
      } : {
        x: 0
      }} transition={{
        duration: 0.36
      }} className="rounded-2xl border border-shell-border bg-shell-surface p-6 shadow-xl shadow-black/10" noValidate data-id="element-64" data-mp="27tnh2s6mwmog">
          <label htmlFor="site-password" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-shell-faint" data-id="element-65" data-mp="2qqf0r2q393t1">
            Team password
          </label>
          <div className="relative" data-id="element-66" data-mp="wc4bit5l07mq">
            <input id="site-password" ref={inputRef} type={reveal ? 'text' : 'password'} autoComplete="current-password" autoFocus value={value} onChange={event => {
            setValue(event.target.value);
            if (status === 'error') setStatus('idle');
          }} aria-invalid={status === 'error'} aria-describedby={status === 'error' ? 'password-error' : undefined} placeholder="••••••••••••" className="w-full rounded-xl border border-shell-border bg-shell-surface-alt px-4 py-3 pr-12 text-sm text-shell-text outline-none transition placeholder:text-shell-faint focus:border-accent focus:ring-2 focus:ring-accent" data-id="element-67" data-mp="wd5x3rk84cip" />
            <button type="button" onClick={() => setReveal(prev => !prev)} aria-label={reveal ? 'Hide password' : 'Show password'} className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-2 text-shell-faint transition hover:bg-shell-surface hover:text-shell-text focus:outline-none focus-visible:ring-2 focus-visible:ring-accent" data-id="element-68" data-mp="2bbjncj3fmwaa">
              {reveal ? <EyeOffIcon className="h-4 w-4" aria-hidden="true" data-id="element-69" data-mp="3fzvpmdnd5ntr" /> : <EyeIcon className="h-4 w-4" aria-hidden="true" data-id="element-70" data-mp="3b0j0tncxm6oj" />}
            </button>
          </div>

          <div aria-live="polite" className="min-h-[20px]" data-id="element-71" data-mp="361y07mtrpkzk">
            {status === 'error' && <p id="password-error" className="mt-2 text-xs font-medium text-red-500" data-id="element-72" data-mp="320ct6i3x00xa">
                That password isn&apos;t right. Check the pinned message in #sales-source.
              </p>}
          </div>

          <button type="submit" disabled={status === 'checking' || value.trim().length === 0} className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-accent px-4 py-3 text-sm font-semibold text-accent-on transition hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-shell-bg disabled:cursor-not-allowed disabled:opacity-40" data-id="element-73" data-mp="2sat9594gu10u">
            {status === 'checking' ? <>
                <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-74" data-mp="xvnul6hzuqp3" />
                Verifying
              </> : 'Unlock workspace'}
          </button>

          <p className="mt-4 flex items-start gap-2 text-[11px] leading-relaxed text-shell-faint" data-id="element-75" data-mp="a48p54ux7t9m">
            <ShieldCheckIcon className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-76" data-mp="f3bivt5gad2a" />
            Access is logged for this session only. Don&apos;t forward this link outside the
            revenue team.
          </p>
        </motion.form>
      </motion.div>
    </main>;
}